/** Where a completed signup came from the article reading prompt. */
export const ARTICLE_SIGNUP_POPUP_SOURCE = "article_signup_popup";

const ATTRIBUTION_KEY = "akinda.articleSignupAttribution";
const SHOWN_KEY = "akinda.articleSignupPrompt.shown";

/** Email verification can finish later in the same browser. */
const ATTRIBUTION_TTL_MS = 24 * 60 * 60 * 1000;

export interface ArticleSignupAttribution {
  article_slug: string;
  article_id: string;
  clicked_at: number;
}

function canUseStorage(storage: Storage | undefined): storage is Storage {
  return typeof storage !== "undefined";
}

export function wasArticleSignupPromptShown(): boolean {
  if (!canUseStorage(globalThis.sessionStorage)) return false;
  try {
    return sessionStorage.getItem(SHOWN_KEY) === "1";
  } catch {
    return false;
  }
}

export function markArticleSignupPromptShown(): void {
  if (!canUseStorage(globalThis.sessionStorage)) return;
  try {
    sessionStorage.setItem(SHOWN_KEY, "1");
  } catch {
    // Private mode: the prompt can still show, it just may repeat next navigation.
  }
}

export function rememberArticleSignupAttribution(
  attribution: Omit<ArticleSignupAttribution, "clicked_at"> & {
    clicked_at?: number;
  },
): void {
  if (!canUseStorage(globalThis.localStorage)) return;
  const payload: ArticleSignupAttribution = {
    article_slug: attribution.article_slug,
    article_id: attribution.article_id,
    clicked_at: attribution.clicked_at ?? Date.now(),
  };
  try {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(payload));
  } catch {
    // Signup still works; this session just won't be attributed.
  }
}

function readAttribution(now = Date.now()): ArticleSignupAttribution | null {
  if (!canUseStorage(globalThis.localStorage)) return null;
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ArticleSignupAttribution>;
    if (
      typeof parsed.article_slug !== "string" ||
      parsed.article_slug.length === 0 ||
      typeof parsed.clicked_at !== "number"
    ) {
      return null;
    }
    if (now - parsed.clicked_at > ATTRIBUTION_TTL_MS) return null;
    return {
      article_slug: parsed.article_slug,
      article_id: typeof parsed.article_id === "string" ? parsed.article_id : "",
      clicked_at: parsed.clicked_at,
    };
  } catch {
    return null;
  }
}

/** Read and delete the pending article-popup attribution, if it is still fresh. */
export function consumeArticleSignupAttribution(
  now = Date.now(),
): ArticleSignupAttribution | null {
  const attribution = readAttribution(now);
  clearArticleSignupAttribution();
  return attribution;
}

export function clearArticleSignupAttribution(): void {
  if (!canUseStorage(globalThis.localStorage)) return;
  try {
    localStorage.removeItem(ATTRIBUTION_KEY);
  } catch {
    // Ignore storage failures.
  }
}
